package dev.wyns.ventrova.data

import dev.wyns.ventrova.data.model.OpeningHours
import dev.wyns.ventrova.data.model.TimeRange
import kotlinx.datetime.DayOfWeek

/**
 * Parses the common subset of the OSM `opening_hours` syntax: weekday selectors (`Mo-Fr`,
 * `Mo,We`, wrapping ranges like `Fr-Mo`), time ranges (also past midnight), `off`/`closed`, `24/7`
 * and comments. Public holiday rules are ignored. Returns null for anything else (months, weeks,
 * sunrise, open end, …), so unknown hours are never shown as wrong hours.
 *
 * See https://wiki.openstreetmap.org/wiki/Key:opening_hours/specification.
 */
object OpeningHoursParser {

    private val dayCodes = mapOf(
        "Mo" to DayOfWeek.MONDAY,
        "Tu" to DayOfWeek.TUESDAY,
        "We" to DayOfWeek.WEDNESDAY,
        "Th" to DayOfWeek.THURSDAY,
        "Fr" to DayOfWeek.FRIDAY,
        "Sa" to DayOfWeek.SATURDAY,
        "Su" to DayOfWeek.SUNDAY,
    )
    private val holidayCodes = setOf("PH", "SH")

    private const val DAY = "(?:Mo|Tu|We|Th|Fr|Sa|Su|PH|SH)"
    private val daySelector = Regex("""^($DAY(?:\s*-\s*$DAY)?(?:\s*,\s*$DAY(?:\s*-\s*$DAY)?)*)\s*:?\s*(.*)$""")
    private val timeRange = Regex("""^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$""")
    private val comment = Regex("\"[^\"]*\"")

    fun parse(raw: String?): OpeningHours? {
        val source = raw?.replace(comment, "")?.trim().orEmpty()
        if (source.isEmpty()) return null
        if (source == "24/7") return OpeningHours(DayOfWeek.entries.associateWith { listOf(TimeRange(0, 24 * 60)) })

        val week = mutableMapOf<DayOfWeek, List<TimeRange>>()
        for (rule in source.replace("||", ";").split(';').map { it.trim() }.filter { it.isNotEmpty() }) {
            val match = daySelector.find(rule)
            val (days, rest) = if (match != null) {
                val days = parseDays(match.groupValues[1]) ?: return null
                // A rule that only targets public/school holidays doesn't affect the regular week.
                if (days.isEmpty()) continue
                days to match.groupValues[2].trim()
            } else {
                DayOfWeek.entries.toSet() to rule
            }
            val ranges = parseTimes(rest) ?: return null
            days.forEach { week[it] = ranges }
        }
        return OpeningHours(week)
    }

    /** Returns the regular weekdays in [selector], skipping holiday codes; null when malformed. */
    private fun parseDays(selector: String): Set<DayOfWeek>? {
        val days = mutableSetOf<DayOfWeek>()
        for (part in selector.split(',').map { it.trim() }) {
            val bounds = part.split('-').map { it.trim() }
            if (bounds.any { it in holidayCodes }) {
                if (bounds.size > 1) return null
                continue
            }
            val from = dayCodes[bounds.first()] ?: return null
            val to = dayCodes[bounds.last()] ?: return null
            var day = from
            while (true) {
                days += day
                if (day == to) break
                day = DayOfWeek.entries[(day.ordinal + 1) % 7]
            }
        }
        return days
    }

    /** Parses `10:00-14:00,17:00-22:00`, `off` or an empty string (open all day). */
    private fun parseTimes(text: String): List<TimeRange>? {
        when (text.lowercase()) {
            "" -> return listOf(TimeRange(0, 24 * 60))
            "off", "closed" -> return emptyList()
        }
        return text.split(',').map { part ->
            val match = timeRange.find(part.trim()) ?: return null
            val (startHour, startMinute, endHour, endMinute) = match.destructured.toList().map { it.toInt() }
            if (startMinute > 59 || endMinute > 59 || startHour > 24 || endHour > 48) return null
            val start = startHour * 60 + startMinute
            var end = endHour * 60 + endMinute
            if (end <= start) end += 24 * 60
            if (start > 24 * 60) return null
            TimeRange(start, end)
        }
    }
}
